import { Body, VTooltip, VTooltipContent, VTooltipTrigger, cn } from '@vrzl/ui';
import { useAtomValue } from 'jotai';
import { AlertTriangle, Check, ChevronDown, X, type LucideIcon } from 'lucide-react';
import { useState } from 'react';

import type { ValidationReportItem } from '../api/types';
import { printModeAtom } from '../state/printMode.atom';
import { criterionSummary } from '../utils/criterionFindings';
import { criterionStatus, type CriterionStatus } from '../utils/validationView';

import { CriterionDetail } from './criterionDetail';
import { CriterionFeedbackButton } from './criterionFeedbackButton';

// Round icon, colours and tooltip wording as the status indicators on the Information screen.
const STATUS_ICON: Record<
  CriterionStatus,
  { icon: LucideIcon; className: string; label: string; description: string }
> = {
  present: {
    icon: Check,
    className: 'bg-success-100 text-success-600',
    label: 'Aanwezig',
    description: 'Dit criterium is aanwezig in de documenten.',
  },
  inconsistent: {
    icon: AlertTriangle,
    className: 'bg-warning-100 text-warning-600',
    label: 'Inconsistent',
    description:
      'Dit criterium geeft tegenstrijdige waarden in de documenten. Controleer de bronnen.',
  },
  missing: {
    icon: X,
    className: 'bg-error-100 text-error-600',
    label: 'Niet aanwezig',
    description: 'Dit criterium is niet aanwezig in de documenten.',
  },
};

type ValidationCriterionRowProps = {
  item: ValidationReportItem;
  eventId: string;
  feedbackSent: boolean;
  onFeedbackSent: (criterionId: string) => void;
};

/**
 * Eén criterium met al zijn vondsten; klikken op de rij klapt hem open of dicht.
 * De feedbackknop staat in de rij, vóór de pijl, maar naast de klapknop: een knop
 * in een knop kan niet, en zo klapt klikken op de tekstballon de rij niet.
 */
export function ValidationCriterionRow({
  item,
  eventId,
  feedbackSent,
  onFeedbackSent,
}: ValidationCriterionRowProps) {
  const [userOpen, setOpen] = useState(false);
  const printMode = useAtomValue(printModeAtom);
  const open = printMode || userOpen;

  const status = criterionStatus(item);
  const {
    icon: Icon,
    className: iconClass,
    label: statusLabel,
    description: statusDescription,
  } = STATUS_ICON[status];

  return (
    <li className="rounded-lg border border-border bg-card shadow-sm">
      {/* Anchor for the chevron and feedback button: the header only, so they stay put when the row opens. */}
      <div className="relative">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex w-full min-w-0 items-center gap-3 rounded-lg py-3 pl-4 pr-20 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {/* Hover only: a focusable trigger cannot sit in this button. Keyboard and screen reader users get the status from the sr-only label. */}
          <VTooltip>
            <VTooltipTrigger asChild>
              <span
                className={cn(
                  'flex size-7 shrink-0 cursor-help items-center justify-center rounded-full',
                  iconClass,
                )}
              >
                <Icon size={16} aria-hidden="true" />
                <span className="sr-only">{statusLabel}</span>
              </span>
            </VTooltipTrigger>
            <VTooltipContent>
              <div className="font-medium">{statusLabel}</div>
              <div className="mt-1 text-muted-foreground">{statusDescription}</div>
            </VTooltipContent>
          </VTooltip>
          <span className="min-w-0 flex-1">
            <Body as="span" weight="medium" className="block">
              {item.name}
            </Body>
            <Body
              as="span"
              tone="muted"
              className={cn('block truncate', status === 'missing' && 'italic')}
            >
              {criterionSummary(item)}
            </Body>
          </span>
          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 print:hidden">
            <ChevronDown
              size={16}
              aria-hidden="true"
              className={cn('text-muted-foreground transition-transform', open && 'rotate-180')}
            />
          </span>
        </button>

        <div className="pointer-events-none absolute inset-y-0 right-10 flex items-center print:hidden">
          <div className="pointer-events-auto">
            <CriterionFeedbackButton
              criterionId={item.id}
              criterionName={item.name}
              criterionCode={item.code}
              eventId={eventId}
              feedbackSent={feedbackSent}
              onFeedbackSent={onFeedbackSent}
            />
          </div>
        </div>
      </div>

      {open && (
        <div className="pb-3 pl-14 pr-4">
          <CriterionDetail item={item} status={status} eventId={eventId} />
        </div>
      )}
    </li>
  );
}
